import { useState, useRef, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Col, Row } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import FullLogo from '../assets/logos/full-logo.png'
import {
  FooterContent,
  Typography,
  HeroBannerSection,
  BannerDecorCircleLarge,
  BannerDecorCircleSmall,
  InfoCard,
  RequirementsIconCircle,
  DetailLabel,
  CardIconBox,
} from '../components/styled'
import storageApi from '../services/firebaseStorage'
import AbstractForm from '../components/forms/AbstractForm'
import FormDiscardButton from '../components/buttons/FormDiscardButton'
import PrimaryButton from '../components/buttons/PrimaryButton'
import Spinner from 'react-bootstrap/Spinner'
import theme from '../constants/theme'
import apiApplications from '../services/api/applications'
import NotificationBoxPage from '../components/notificationBoxPage'
import {
  noticePeriodOptions,
  source,
  genderOptions,
} from '../constants/candidate'
import ProgressBar from '../components/progress-bar/progressBar'
import {
  getFormattedDate,
  currency,
  educationLevel,
  travelFreq,
  languages,
} from '../constants/position'
import {
  KEY_SUPPORTED_FORMATS,
  MAX_FILE_SIZE,
  formatCountryName,
} from '../utilities/stringUtilities'
import apiInternalApplications from '../services/api/internalApplications'
import LanguagesDropdown from '../components/languageDropdown'
import EliminatingQuestion from '../components/EliminatingQuestions'
import apiPositionEliminatingQuestions from '../services/api/positionEliminatingQuestions'
import GeoAlt from '../assets/svgs/GeoAlt'
import Mortarboard from '../assets/svgs/Mortarboard'
import Briefcase from '../assets/svgs/Briefcase'
import FileUser from '../assets/svgs/FileText'
import Plane from '../assets/svgs/Plane'
import Question from '../assets/svgs/Question'
import ProfileAvatar from '../components/profileAvatar'
import Translate from '../assets/svgs/Translate'
import CurrencyDollar from '../assets/svgs/CurrencyDollar'
import EmploymentType from '../assets/svgs/EmploymentType'
import UserLock from '../assets/svgs/UserLock'
import {
  track,
  startSessionRecording,
  stopSessionRecording,
} from '../services/analytics'
import Clock from '../assets/svgs/clock'

function FormSubmitCapture({ submitRef, onSubmit }) {
  const { handleSubmit } = useFormContext()
  submitRef.current = () => handleSubmit(onSubmit)()
  return null
}

FormSubmitCapture.propTypes = {
  submitRef: PropTypes.shape({ current: PropTypes.func }).isRequired,
  onSubmit: PropTypes.func.isRequired,
}

function JobApplicationForm({
  onBackClick,
  company,
  position,
  isInternalHiring,
  isEasyApply = false,
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language
  const { token } = useParams()
  const navigate = useNavigate()
  const [renderType, setRenderType] = useState('form')
  const [message, setMessage] = useState('')
  const [eliminatingQuestions, setEliminatingQuestions] = useState([])
  const [eliminatingAnswers, setEliminatingAnswers] = useState([])
  const formRef = useRef(null)
  const [formIsLoading, setFormIsLoading] = useState(false)

  useEffect(() => {
    startSessionRecording()
    return () => stopSessionRecording()
  }, [])

  useEffect(() => {
    apiPositionEliminatingQuestions
      .getAllByPosition(position?.id)
      .then((res) => {
        setEliminatingQuestions(res)
      })
  }, [position])

  const PHONE_REG_EXP =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

  const progressRef = useRef(null)
  const errorRef = useRef(null)

  const areEliminatingQuestionsAnswered = (answers) =>
    answers && answers?.length === eliminatingQuestions?.length

  const handleClick = () => {
    onBackClick()
  }

  const handleUploadProgress = (node, progress) => {
    const progressNode = node
    progressNode.setAttribute('aria-valuenow', progress)
    progressNode.setAttribute(
      'style',
      `width: ${progress}%; background-color: ${theme.talentinoBlue}`,
    )
    progressNode.innerText = `${progress}%`
  }

  const handleSuccessfulUpload = (data, setIsLoading) => {
    handleUploadProgress(progressRef.current, 100)

    setTimeout(() => {
      let applicationRes = null
      const allData =
        eliminatingAnswers?.length > 0
          ? {
              ...data,
              eliminatingQuestionsAnswer: eliminatingAnswers,
              ...(isEasyApply && { is_easy_apply: true }),
            }
          : {
              ...data,
              ...(isEasyApply && { is_easy_apply: true }),
            }
      if (isInternalHiring)
        applicationRes = apiInternalApplications.applyForPositionInternally
      else applicationRes = apiApplications.applyForPosition

      applicationRes(token, allData)
        .then((res) => {
          const is_found_app = isInternalHiring
            ? res?.message === 'found'
            : res[0]?.message === 'found'
          if (is_found_app) {
            setRenderType('error')
            setMessage(t('pages.applicationSubmitMsg'))
          } else {
            setRenderType('success')
            !isInternalHiring && res[0]?.token
              ? navigate(`/calendar/${res[0].token}`)
              : setMessage(t('pages.applyMsg', { position: position.title }))
          }
        })

        .catch((err) => console.error(err))
        .finally(() => setIsLoading(false))
    }, 500)
  }

  const handleUploadError = (error) => {
    errorRef.current.innerText = error
  }

  const [requiredQuestions, setRequiredQuestions] = useState(false)
  const handleSubmit = async (data, setIsLoading) => {
    if (!data.resumes.length) {
      alert(t('validation.resumes'))
      setIsLoading(false)
      return
    }
    if (
      eliminatingQuestions?.length > 0 &&
      !areEliminatingQuestionsAnswered(eliminatingAnswers)
    ) {
      setRequiredQuestions(true)
      setIsLoading(false)
      return
    }
    storageApi.uploadFile(
      data.resumes[0],
      (progress) => handleUploadProgress(progressRef.current, progress),
      (url) =>
        handleSuccessfulUpload(
          {
            ...data,
            resumes: [url],
          },
          setIsLoading,
        ),
      (err) => handleUploadError(err),
    )
  }

  const externalHiringInputs = [
    {
      name: 'firstName',
      label: t('form.firstName'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'lastName',
      label: t('form.lastName'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'email',
      label: t('form.email'),
      type: 'email',
      required: true,
      width: 6,
    },
    {
      name: 'phone',
      label: t('form.phone'),
      type: 'number',
      required: true,
      width: 6,
    },
    {
      name: 'gender',
      label: t('form.gender'),
      renderType: 'select',
      required: false,
      options: genderOptions.map((option) => ({
        value: option.value,
        label: option[locale],
      })),
      width: 6,
    },
    {
      name: 'nationality',
      label: t('form.nationality'),
      type: 'text',
      required: false,
      width: 6,
    },
    {
      name: 'address',
      label: t('form.address'),
      type: 'text',
      placeholder: t('form.addressPlaceholder'),
      required: true,
      width: 6,
    },
    {
      name: 'title',
      label: t('form.title'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'experienceYears',
      type: 'number',
      label: t('form.yearsOfExperience'),
      required: true,
      width: 6,
    },
    {
      name: 'source',
      label: t('form.foundJobOn'),
      renderType: 'select',
      required: true,
      options: source.map((option) => ({
        value: option.value,
        label: option[locale],
      })),
      width: 6,
    },
    {
      name: 'currentSalary',
      type: 'number',
      label: t('form.currentSalary'),
      required: false,
      width: 3,
    },
    {
      name: 'expectedSalary',
      type: 'number',
      label: t('form.expectedSalary'),
      required: true,
      width: 3,
    },
    {
      name: 'currency',
      renderType: 'select',
      label: t('form.currency'),
      options: currency,
      width: 3,
    },
    {
      name: 'noticePeriod',
      label: t('form.noticePeriod'),
      renderType: 'select',
      required: true,
      options: noticePeriodOptions.map((option) => ({
        value: option.value,
        label: option[locale],
      })),
      width: 3,
    },
    {
      name: 'resumes',
      label: t('form.resumes'),
      renderType: 'files',
      required: true,
      width: 12,
      accept: '.doc*, .dot*, .pdf',
    },
  ]

  const internalHiringInputs = [
    {
      name: 'firstName',
      label: t('form.firstName'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'lastName',
      label: t('form.lastName'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'employeeId',
      label: t('form.employeeId'),
      type: 'text',
      required: true,
      width: 12,
    },
    {
      name: 'email',
      label: t('form.email'),
      type: 'email',
      required: true,
      width: 6,
    },
    {
      name: 'phone',
      label: t('form.phone'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'gender',
      label: t('form.gender'),
      renderType: 'select',
      required: false,
      options: genderOptions.map((option) => ({
        value: option.value,
        label: option[locale],
      })),
      width: 6,
    },
    {
      name: 'officeAddress',
      label: t('form.officeAddress'),
      type: 'text',
      placeholder: t('form.addressPlaceholder'),
      required: true,
      width: 6,
    },
    {
      name: 'currentTitle',
      label: t('form.currentTitle'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'joined',
      label: t('form.joinedSince', {
        company:
          company?.showClientDetails !== false
            ? company?.companyName
            : t('organization.oneOfOurClientsIsHiring'),
      }),
      type: 'date',
      max: getFormattedDate(new Date()),
      required: true,
      width: 6,
    },
    {
      name: 'currentDepartment',
      label: t('form.currentDepartment'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'resumes',
      label: t('form.resumes'),
      renderType: 'files',
      required: true,
      width: 12,
      accept: '.doc*, .dot*, .pdf',
    },
  ]

  const easyApplyInputs = [
    {
      name: 'resumes',
      label: t('form.resumes'),
      renderType: 'files',
      required: true,
      width: 12,
      accept: '.doc*, .dot*, .pdf',
    },
  ]

  const handleFormSubmit = (data) => {
    track('Application Form Submitted', {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      position_id: position?.id,
      phone_number: data.phone,
      gender: data.gender,
      nationality: data.nationality,
      address: data.address,
      title: data.title,
      years_of_experience: data.experienceYears,
      source: data.source,
      current_monthly_salary: data.currentSalary,
      expected_monthly_salary: data.expectedSalary,
      currency: data.currency,
      notice_period: data.noticePeriod,
      attach_resume: Array.from(data.resumes || []).map(
        (file) => file?.name ?? file,
      ),
      is_first_application: data.isFirstApplication,
    })
    setFormIsLoading(true)
    handleSubmit(data, setFormIsLoading)
  }

  const externalHiringSchema = Yup.object().shape({
    firstName: Yup.string().required(t('general.required')),
    lastName: Yup.string().required(t('general.required')),
    email: Yup.string().email().required(t('general.required')),
    phone: Yup.string()
      .matches(PHONE_REG_EXP, `${t('validation.phone')}`)
      .required(t('general.required')),
    gender: Yup.string(),
    nationality: Yup.string(),
    title: Yup.string().required(t('general.required')),
    address: Yup.string().required(t('general.required')),
    source: Yup.string().required(t('general.required')),
    experienceYears: Yup.number(t('validation.number'))
      .typeError(t('validation.number'))
      .integer(t('validation.integer'))
      .min(0, t('validation.positive'))
      .required(t('general.required')),
    currentSalary: Yup.number(t('validation.number'))
      .typeError(t('validation.number'))
      .integer(t('validation.integer'))
      .positive(t('validation.invalidSalary'))
      .nullable()
      .transform((v, o) => (o === '' ? null : v)),
    expectedSalary: Yup.number(t('validation.number'))
      .typeError(t('validation.number'))
      .integer(t('validation.integer'))
      .positive(t('validation.invalidSalary'))
      .required(t('general.required')),
    noticePeriod: Yup.string().required(t('general.required')),
    resumes: Yup.mixed()
      .required(t('general.required'))
      .test(
        'fileUpload',
        t('general.required'),
        (value) => Object.keys(value).length,
      )
      .test(
        'fileUpload',
        t('validation.fileNumber'),
        (value) => Object.keys(value).length === 1,
      )
      .test(
        'fileSize',
        t('validation.fileSize'),
        (files) =>
          Object.keys(files).filter((file) => files[file].size <= MAX_FILE_SIZE)
            .length,
      )
      .test(
        'fileFormat',
        t('validation.fileFormate'),
        (files) =>
          Object.keys(files).filter(
            (file) => files[file].type === KEY_SUPPORTED_FORMATS.pdf,
          ).length,
      ),
  })

  const internalHiringSchema = Yup.object().shape({
    firstName: Yup.string().required(t('general.required')),
    lastName: Yup.string().required(t('general.required')),
    employeeId: Yup.string().required(t('general.required')),
    email: Yup.string().email().required(t('general.required')),
    phone: Yup.string()
      .matches(PHONE_REG_EXP, t('validation.phone'))
      .required(t('general.required')),
    gender: Yup.string(),
    currentTitle: Yup.string().required(t('general.required')),
    officeAddress: Yup.string().required(t('general.required')),
    joined: Yup.string().required(t('general.required')),
    currentDepartment: Yup.string().required(t('general.required')),
    resumes: Yup.mixed()
      .required(t('general.required'))
      .test(
        'fileUpload',
        t('general.required'),
        (value) => Object.keys(value).length,
      )
      .test(
        'fileUpload',
        t('validation.fileNumber'),
        (value) => Object.keys(value).length === 1,
      )
      .test(
        'fileSize',
        t('validation.fileSize'),
        (files) =>
          Object.keys(files).filter((file) => files[file].size <= MAX_FILE_SIZE)
            .length,
      )
      .test(
        'fileFormat',
        t('validation.fileFormate'),
        (files) =>
          Object.keys(files).filter(
            (file) => files[file].type === KEY_SUPPORTED_FORMATS.pdf,
          ).length,
      ),
  })

  const easyApplySchema = Yup.object().shape({
    resumes: Yup.mixed()
      .test(
        'required',
        t('validation.resumes'),
        (files) => Object.keys(files).length > 0,
      )
      .test(
        'fileSize',
        t('validation.fileSize'),
        (files) =>
          Object.keys(files).filter((file) => files[file].size > MAX_FILE_SIZE)
            .length === 0,
      )
      .test(
        'fileFormat',
        t('validation.fileFormate'),
        (files) =>
          Object.keys(files).filter(
            (file) => files[file].type === KEY_SUPPORTED_FORMATS.pdf,
          ).length,
      ),
  })

  const defaultValues = useMemo(
    () =>
      isInternalHiring
        ? {
            gender: '',
          }
        : {
            noticePeriod: noticePeriodOptions[0].value,
            source: source[0].value,
            gender: '',
          },
    [isInternalHiring, noticePeriodOptions, source],
  )

  const getFormInputs = () => {
    if (isEasyApply) return easyApplyInputs
    if (isInternalHiring) return internalHiringInputs
    return externalHiringInputs
  }

  const getValidationSchema = () => {
    if (isEasyApply) return easyApplySchema
    if (isInternalHiring) return internalHiringSchema
    return externalHiringSchema
  }

  const displayCompanyName =
    company?.showClientDetails !== false
      ? company?.companyName
      : t('organization.oneOfOurClientsIsHiring')

  const agencyOrCompanyName = position?.agency?.name || displayCompanyName || ''

  const mapValueToLabel = (list, value) => {
    const found = list?.find((f) => f.value === value)
    return found ? found[locale] || found.en : ''
  }

  const positionSummaryDetails = [
    {
      label: t('pages.language'),
      value: Array.isArray(position?.language)
        ? position.language
            .map((lang) => mapValueToLabel(languages, lang))
            .filter(Boolean)
            .join(', ')
        : mapValueToLabel(languages, position?.language),
      Icon: Translate,
    },
    {
      label: t('pages.educationLevel'),
      value: mapValueToLabel(educationLevel, position?.educationLevel),
      Icon: Mortarboard,
    },
    {
      label: t('pages.travelFreq'),
      value: mapValueToLabel(travelFreq, position?.travelFreq),
      Icon: Plane,
    },
    {
      label: t('pages.location'),
      value: formatCountryName(position?.country),
      Icon: GeoAlt,
    },
    !position?.hideSalary && position?.budget
      ? {
          label: t('pages.salary'),
          value: `${position.budget} ${position.currency}`,
          Icon: CurrencyDollar,
        }
      : 'null',
    !position?.hideSalary && position?.additionalSalary
      ? {
          label: t('pages.additionalSalary'),
          value: `${position.additionalSalary} `,
          Icon: CurrencyDollar,
        }
      : 'null',
    {
      label: t('pages.workplaceType'),
      value: position?.workplaceType,
      Icon: Briefcase,
    },
    {
      label: t('pages.employmentType'),
      value: position?.employmentType,
      Icon: EmploymentType,
    },
    position?.employmentType === 'Project-Based' &&
    position?.projectDuration?.number
      ? {
          label: t('pages.projectDuration'),
          value:
            `${position.projectDuration.number} ${position.projectDuration.unit ?? ''}`.trim(),
          Icon: Clock,
        }
      : 'null',
    {
      label: t('pages.seniorityLevel'),
      value: position?.seniorityLevel,
      Icon: UserLock,
    },
  ].filter((f) => f && f.value)

  if (renderType === 'error')
    return <NotificationBoxPage messageType="error" message={message} />

  if (renderType === 'success')
    return <NotificationBoxPage messageType="success" message={message} />

  return (
    <>
      <div style={{ backgroundColor: '#F1F5FA', overflowX: 'clip' }}>
        {/* ========header */}
        <Row
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: theme.white,
            padding: '0px 20px',
          }}
        >
          <Col xs="auto">
            <img height="auto" width="150px" src={FullLogo} alt="" />
          </Col>
          <Col />
          <Col xs="auto" className="mt-3">
            <LanguagesDropdown />
          </Col>
        </Row>

        <Row style={{ margin: '0px clamp(0px, 5vw, 70px)' }} className="mt-3">
          {/* Banner */}
          <HeroBannerSection
            style={{ padding: '20px 24px', marginBottom: '20px' }}
          >
            <BannerDecorCircleLarge />
            <BannerDecorCircleSmall />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <Typography
                  size="13px"
                  color="rgba(255,255,255,0.8)"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <FileUser width="13" height="13" />
                  {t('pages.applicationYourDetails')}
                </Typography>
                <Typography
                  size="22px"
                  bold
                  color="#ffffff"
                  style={{ display: 'block' }}
                >
                  {position?.title}
                </Typography>
                <Typography
                  size="13px"
                  color="rgba(255,255,255,0.8)"
                  style={{ display: 'block', marginTop: '4px' }}
                >
                  {t('pages.at')} {agencyOrCompanyName}
                </Typography>
              </div>
            </div>
          </HeroBannerSection>
        </Row>
        <Row className="mt-1 pb-2 justify-content-center mx-0">
          <Col xs={12} lg={7} className="mb-4 mb-lg-0 px-2">
            {isInternalHiring && (
              <InfoCard className="p-4 border-t border--primary ">
                {isInternalHiring ? (
                  <div>
                    <Typography
                      size="20px"
                      bold
                      color={theme.talentinoBlack}
                      style={{ display: 'block' }}
                    >
                      {t('pages.hello')}!
                    </Typography>
                    <Typography
                      size="14px"
                      regular
                      style={{
                        display: 'block',
                        marginTop: '8px',
                        color: theme.talentinoTextGray,
                      }}
                    >
                      {t('pages.helloMsg', { company: displayCompanyName })}
                    </Typography>
                    <Typography
                      size="14px"
                      regular
                      style={{
                        display: 'block',
                        marginTop: '8px',
                        color: theme.talentinoTextGray,
                      }}
                    >
                      {t('pages.thanksMsg')}
                    </Typography>
                    <div className="d-flex gap-2 mt-4">
                      <ProfileAvatar name={displayCompanyName} />
                      <div className="d-flex flex-column">
                        <Typography size="16px">
                          {displayCompanyName}
                        </Typography>
                        <Typography
                          regular
                          size="12px"
                          color={theme.talentinoTextGray}
                        >
                          {t('pages.recruitingTeam')}
                        </Typography>
                      </div>
                    </div>
                  </div>
                ) : null}
              </InfoCard>
            )}

            <InfoCard className={`p-4 ${isInternalHiring ? 'mt-3' : ''} `}>
              {/* Apply Your Application heading */}
              <div style={{ marginBottom: '16px' }}>
                <Typography
                  size="18px"
                  bold
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <CardIconBox>
                    <FileUser
                      width="15"
                      height="15"
                      color={theme.brandColorOrange}
                    />
                  </CardIconBox>
                  {t('pages.applyYourApplication')}
                </Typography>
              </div>

              {/* AbstractForm – actions rendered outside the InfoCard */}
              <AbstractForm
                inputs={getFormInputs()}
                schema={getValidationSchema()}
                defaultValues={defaultValues}
              >
                <FormSubmitCapture
                  submitRef={formRef}
                  onSubmit={handleFormSubmit}
                />
              </AbstractForm>
            </InfoCard>
            <InfoCard className="p-4 mt-3">
              {eliminatingQuestions.length > 0 && (
                <Typography
                  size="18px"
                  bold
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <CardIconBox>
                    <Question
                      width="15"
                      height="15"
                      color={theme.brandColorOrange}
                    />
                  </CardIconBox>
                  {t('pages.requiredQuestions')}
                </Typography>
              )}
              {eliminatingQuestions?.map((question, index) => (
                <EliminatingQuestion
                  key={question.id}
                  question={question}
                  index={index + 1}
                  setEliminatingAnswers={setEliminatingAnswers}
                />
              ))}
              {requiredQuestions && (
                <Typography
                  size="12px"
                  regular
                  style={{
                    display: 'block',
                    marginTop: '8px',
                    color: theme.talentinoRed,
                  }}
                >
                  {t('validation.questionsRequired')}
                </Typography>
              )}
              <ProgressBar
                ref={{
                  progressRef,
                  errorRef,
                }}
              />
            </InfoCard>

            {/* Action buttons – outside all InfoCards */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginTop: '12px',
                justifyContent: 'flex-end',
              }}
            >
              <FormDiscardButton
                text={t('general.back')}
                handleClick={handleClick}
                isLoading={formIsLoading}
                style={{ minWidth: '100px' }}
              />
              <PrimaryButton
                backgroundcolor={theme.brandColorOrange}
                hovercolor={theme.brandColorOrange}
                disabled={formIsLoading}
                onClick={() => formRef.current?.()}
                width="auto"
                style={{ minWidth: '100px' }}
              >
                {formIsLoading && (
                  <Spinner
                    animation="border"
                    variant="light"
                    size="sm"
                    className="me-2"
                  />
                )}
                {isEasyApply ? t('pages.easyApply') : t('general.submit')}
              </PrimaryButton>
            </div>
          </Col>

          <Col
            xs={12}
            lg={4}
            className="order-first order-lg-last px-2 mb-3 mb-lg-0"
            style={{ position: 'sticky', top: '80px', alignSelf: 'flex-start' }}
          >
            <InfoCard className="px-4 py-3 mb-3 ">
              <Typography
                size="10px"
                bold
                color={theme.brandColorOrange}
                style={{
                  display: 'block',
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase',
                }}
              >
                {t('pages.applyingFor')}
              </Typography>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  paddingBottom: '12px',
                  marginBottom: '12px',
                  borderBottom: `1px solid ${theme.talentinoWhiteBlue}`,
                }}
              >
                <div>
                  <Typography
                    size="18px"
                    bold
                    color={theme.talentinoBlack}
                    style={{ display: 'block' }}
                  >
                    {position?.title}
                  </Typography>
                  <Typography
                    size="12px"
                    regular
                    color={theme.talentinoDarkerGray}
                    style={{ display: 'block' }}
                  >
                    {agencyOrCompanyName}
                  </Typography>
                </div>
              </div>

              {/* Detail rows */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {positionSummaryDetails.map(({ Icon, label, value }) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <RequirementsIconCircle>
                      <Icon
                        width="13"
                        height="13"
                        color={theme.brandColorOrange}
                      />
                    </RequirementsIconCircle>
                    <DetailLabel style={{ width: '150px', flexShrink: 0 }}>
                      {label}
                    </DetailLabel>
                    <Typography size="13px" bold color={theme.talentinoBlack}>
                      {value}
                    </Typography>
                  </div>
                ))}
              </div>
            </InfoCard>
          </Col>
        </Row>

        <FooterContent className="d-flex py-2 border-t border-gray-200">
          {t('footer.TalentinoAI')} © {new Date().getFullYear()}{' '}
          {t('footer.allRightsReserved')}
        </FooterContent>
      </div>
    </>
  )
}

JobApplicationForm.propTypes = {
  onBackClick: PropTypes.func.isRequired,
  company: PropTypes.shape({
    companyName: PropTypes.string,
    showClientDetails: PropTypes.bool,
  }).isRequired,
  position: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    country: PropTypes.string,
    educationLevel: PropTypes.string,
    travelFreq: PropTypes.string,
    employmentType: PropTypes.string,
    language: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    hideSalary: PropTypes.bool,
    budget: PropTypes.string,
    currency: PropTypes.string,
    additionalSalary: PropTypes.string,
    workplaceType: PropTypes.string,
    projectDuration: PropTypes.shape({
      number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      unit: PropTypes.string,
    }),
    seniorityLevel: PropTypes.string,
    agency: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  isInternalHiring: PropTypes.bool.isRequired,
  isEasyApply: PropTypes.bool,
}

export default JobApplicationForm
